import { faVideo } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import cameraApiService from 'features/cameras/api/camera.api'
import cameraHealthApiService from 'features/cameras/api/camera-health.api'
import { CameraHealthStatus } from 'features/cameras/types/camera-health.types'
import { Camera } from 'domain/camera'
import { useAuth } from 'layout/context/auth-context'
import 'leaflet/dist/leaflet.css'
import { Button } from 'primereact/button'
import { ProgressSpinner } from 'primereact/progressspinner'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet'
import { buildMappableCameras, MappableCamera } from '../utils/build-mappable-cameras'
import { eventsRouteForCamera } from '../utils/events-route-for-camera'
import { statusDivIcon } from '../utils/status-div-icon'
import './camera-map-view.scss'

// Centro do Brasil — usado só quando nenhuma câmera tem coordenadas ainda,
// para o mapa não abrir no meio do oceano em (0, 0).
const BRAZIL_CENTER: [number, number] = [-14.235, -51.9253]
const BRAZIL_ZOOM = 4
const CAMERA_ZOOM = 15

const CameraMapView = () => {
  const { clientId } = useAuth()
  const navigate = useNavigate()

  const [cameras, setCameras] = useState<Camera[]>([])
  const [health, setHealth] = useState<CameraHealthStatus[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!clientId) {
      return
    }

    setIsLoading(true)
    setError(null)

    Promise.all([
      cameraApiService.getCameras({ clientId }),
      // A saúde é um extra (cor do pin) — se falhar, o mapa ainda mostra as
      // câmeras, só sem o status.
      cameraHealthApiService.getCameraHealth(clientId).catch(
        (): { responseObject: { cameras: CameraHealthStatus[] } } => ({
          responseObject: { cameras: [] },
        })
      ),
    ])
      .then(([cameraList, healthResponse]) => {
        setCameras(cameraList.responseObject ?? [])
        setHealth(healthResponse.responseObject?.cameras ?? [])
      })
      .catch(() => {
        setError('Falha ao carregar as câmeras. Por favor, tente novamente.')
      })
      .finally(() => setIsLoading(false))
  }, [clientId])

  const mappableCameras = useMemo(
    () => buildMappableCameras(cameras, health),
    [cameras, health]
  )

  const camerasWithoutLocation = cameras.length - mappableCameras.length

  const mapCenter = useMemo((): [number, number] => {
    if (mappableCameras.length === 0) {
      return BRAZIL_CENTER
    }

    const avgLat =
      mappableCameras.reduce((sum, c) => sum + c.latitude, 0) /
      mappableCameras.length
    const avgLng =
      mappableCameras.reduce((sum, c) => sum + c.longitude, 0) /
      mappableCameras.length

    return [avgLat, avgLng]
  }, [mappableCameras])

  const mapZoom = mappableCameras.length === 0 ? BRAZIL_ZOOM : CAMERA_ZOOM

  return (
    <div className='map-container'>
      <div className='map-header'>
        <h2 className='map-title'>
          <i className='pi pi-map-marker' />
          Mapa de Câmeras
        </h2>
        <p className='map-subtitle'>
          {mappableCameras.length} de {cameras.length} câmeras com
          localização cadastrada
        </p>
      </div>

      {isLoading ? (
        <div
          className='map-state map-state--loading'
          data-testid='map-loading'
        >
          <ProgressSpinner strokeWidth='4' />
        </div>
      ) : error ? (
        <div
          className='map-state map-state--error'
          data-testid='map-error'
        >
          <i className='pi pi-exclamation-triangle' />
          <span>{error}</span>
        </div>
      ) : mappableCameras.length === 0 ? (
        <div
          className='map-state map-state--empty'
          data-testid='map-empty'
        >
          <i className='pi pi-map-marker' />
          <span>Nenhuma câmera com localização cadastrada ainda.</span>
          <p>
            Cadastre o endereço e as coordenadas na tela de Câmeras, no
            passo &quot;Credenciais e Localização&quot;.
          </p>
          <Button
            label='Ir para Câmeras'
            icon='pi pi-video'
            outlined
            onClick={() => navigate('/cameras')}
          />
        </div>
      ) : (
        <div className='map-card'>
          <MapContainer
            // react-leaflet only reads center/zoom on mount — remount when
            // the mappable count changes so a freshly loaded camera list
            // (or one that now has coordinates) actually re-centers.
            key={mappableCameras.length}
            center={mapCenter}
            zoom={mapZoom}
            scrollWheelZoom
            className='map-leaflet-container'
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
            />

            {mappableCameras.map((mappable) => (
              <CameraMarker
                key={mappable.camera.id}
                mappable={mappable}
              />
            ))}
          </MapContainer>
        </div>
      )}

      {camerasWithoutLocation > 0 && !isLoading && !error && (
        <p className='map-footnote'>
          <i className='pi pi-info-circle' />
          {camerasWithoutLocation}{' '}
          {camerasWithoutLocation === 1
            ? 'câmera não aparece no mapa'
            : 'câmeras não aparecem no mapa'}{' '}
          por não ter latitude/longitude cadastradas.
        </p>
      )}
    </div>
  )
}

const CameraMarker: React.FC<{ mappable: MappableCamera }> = ({
  mappable,
}) => {
  const navigate = useNavigate()
  const { camera, latitude, longitude, statusInfo } = mappable

  return (
    <Marker
      position={[latitude, longitude]}
      icon={statusDivIcon(statusInfo.status)}
    >
      <Popup>
        <div className='camera-map-popup'>
          <div className='camera-map-popup__header'>
            <FontAwesomeIcon icon={faVideo} />
            <span>{camera.name}</span>
          </div>

          <span
            className={`camera-map-popup__status camera-map-popup__status--${statusInfo.status}`}
          >
            {statusInfo.label}
          </span>

          {camera.address && (
            <div className='camera-map-popup__address'>{camera.address}</div>
          )}

          <div className='camera-map-popup__meta'>
            <span>{camera.model?.name}</span>
            {camera.model?.capabilities && (
              <span>{camera.model.capabilities}</span>
            )}
          </div>

          <div className='camera-map-popup__actions'>
            <Button
              label='Ver ao vivo'
              icon='pi pi-video'
              size='small'
              onClick={() => navigate(`/stream?camera=${encodeURIComponent(camera.name)}`)}
            />
            <Button
              label='Ver eventos'
              icon='pi pi-list'
              size='small'
              outlined
              onClick={() =>
                navigate(
                  `${eventsRouteForCamera(camera)}?camera=${encodeURIComponent(camera.name)}`
                )
              }
            />
          </div>
        </div>
      </Popup>
    </Marker>
  )
}

export default CameraMapView
